import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

interface CityTerminal {
    id: number; 
    city_name: string;
    terminal_code: string;
    terminal_id: string;
    address: string;
    created_by: string;
    created_at: string;
}

interface User {
    id: number;
    username: string;
    pharmacyNum: string;
}

interface CityManagerDialogProps {
    open: boolean;
    onClose: () => void;
}

const CityManagerDialog: React.FC<CityManagerDialogProps> = ({ open, onClose }) => {
    const [selectedPharmacy, setSelectedPharmacy] = useState<User | null>(null);
    const [cityName, setCityName] = useState('');
    const [terminalCode, setTerminalCode] = useState('');
    const [terminalID, setTerminalID] = useState('');
    const [address, setAddress] = useState('');
    const [cityTerminals, setCityTerminals] = useState<CityTerminal[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get('https://tbl.zerdegroup.kz/api/users-with-pharmacy');
                if (response.data.success) {
                    setUsers(response.data.users);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        const fetchCityTerminals = async () => {
            try {
                const response = await axios.get('https://tbl.zerdegroup.kz/api/city-terminals');
                if (response.data.success) {
                    setCityTerminals(response.data.terminals);
                }
            } catch (error) {
                console.error('Error fetching city terminals:', error);
            }
        };

        fetchUsers();
        fetchCityTerminals();
    }, []);

    const handlePharmacyChange = (event: any, newValue: User | null) => {
        setSelectedPharmacy(newValue);
    };

    const handleAddCity = async () => {
        if (!cityName || !terminalCode || !selectedPharmacy || !terminalID || !address) return;

        try {
            const response = await axios.post('https://tbl.zerdegroup.kz/api/city-terminals', {
                city_name: cityName,
                terminal_code: terminalCode,
                created_by: selectedPharmacy.username,
                terminal_id: terminalID,
                address: address
            });

            if (response.data.success) {
                setCityTerminals([...cityTerminals, response.data.terminal]);
                setCityName('');
                setTerminalCode('');
                setTerminalID('');
                setAddress('');
            }
        } catch (error) {
            console.error('Error adding city terminal:', error);
        }
    };

    const handleDeleteCity = async (id: number) => {
        try {
            const response = await axios.delete(`https://tbl.zerdegroup.kz/api/city-terminals/${id}`);
            if (response.data.success) {
                setCityTerminals(cityTerminals.filter(city => city.id !== id));
            }
        } catch (error) {
            console.error('Error deleting city terminal:', error);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Управление городами</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Autocomplete
                        options={users}
                        getOptionLabel={(option) => `${option.username} (Аптека: ${option.pharmacyNum})`}
                        renderInput={(params) => <TextField {...params} label="Выберите аптеку" />}
                        onChange={handlePharmacyChange}
                        value={selectedPharmacy}
                        size='small'
                        sx={{
                            width: '100%',
                        }}
                    />

                    {selectedPharmacy && (
                        <>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <TextField
                                    label="Название города"
                                    value={cityName}
                                    onChange={(e) => setCityName(e.target.value)}
                                    sx={{ flex: '1 1 200px' }}
                                />
                                <TextField
                                    label="Код города"
                                    value={terminalCode}
                                    onChange={(e) => setTerminalCode(e.target.value)}
                                    sx={{ flex: '1 1 150px' }}
                                />
                                <TextField
                                    label="ID"
                                    value={terminalID}
                                    onChange={(e) => setTerminalID(e.target.value)}
                                    sx={{ flex: '1 1 150px' }}
                                />
                            </Box>
                            <TextField
                                label="Адрес аптеки"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                fullWidth
                                required
                                placeholder="Например: г.Актау, Мангистауская область, 15 мкр, д.78"
                                helperText="Точный адрес для жесткого сравнения с данными"
                            />
                            <Button
                                variant="contained"
                                onClick={handleAddCity}
                                disabled={!cityName || !terminalCode || !terminalID || !address}
                            >
                                Добавить
                            </Button>
                        </>
                    )}

                    <TableContainer 
                        component={Paper} 
                        sx={{ 
                            mt: 2,
                            maxHeight: 300,
                            overflow: 'auto'
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Город</TableCell>
                                    <TableCell>Код</TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>Адрес</TableCell>
                                    <TableCell>Аптека</TableCell>
                                    <TableCell>Дата создания</TableCell>
                                    <TableCell>Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cityTerminals.map((city) => (
                                    <TableRow key={city.id}>
                                        <TableCell>{city.city_name}</TableCell>
                                        <TableCell>{city.terminal_code}</TableCell>
                                        <TableCell>{city.terminal_id}</TableCell>
                                        <TableCell sx={{ minWidth: 200 }}>
                                            {city.address || '—'}
                                        </TableCell>
                                        <TableCell>{city.created_by}</TableCell>
                                        <TableCell>
                                            {new Date(city.created_at).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => handleDeleteCity(city.id)}
                                                color="error"
                                                size="small"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Закрыть</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CityManagerDialog; 